/* eslint-disable react-refresh/only-export-components */
import { FunctionComponent, useEffect, useRef, useState } from "react"

import { useModal } from "@/components/core/Root/modal-root"
import Icon from "@/components/Icon"
import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { cn } from "@/helpers/classNames"
import { useI18n } from "@/helpers/i18n/use-translation"
import { times } from "@/helpers/other"
import { useOS } from "@/hooks/useOS"

const MicrophoneErrorModal: FunctionComponent = () => {
  const { os } = useOS()
  const { t } = useI18n()
  const videoRef = useRef<HTMLVideoElement>(null)

  const styles = {
    wrapper: cn("flex flex-row gap-x-6 px-8 text-neutral-700"),
    textContainer: cn("flex w-72 flex-col gap-y-4"),
    buttonsContainer: cn("mt-auto flex justify-between gap-x-4"),
    buttonWrapper: cn(
      "flex items-center justify-between gap-x-1 font-button-small",
    ),
    iconLeft: cn("-rotate-180"),
    subtitle: cn("font-h3-regular"),
    list: cn("list-inside list-decimal font-body-medium-regular"),
    listItem: cn("mb-4"),
    videoContainer: cn("w-[250px] grow"),
  }

  const [osSelection, setOSSelection] = useState<
    ReturnType<typeof useOS>["os"]
  >(os === "mac" ? "mac" : "win")

  const iterations = osSelection === "mac" ? 4 : 3

  const videoSrc = `/videos/${
    osSelection === "mac" ? "mac" : "windows"
  }-sequence.mp4`

  const posterSrc = `/videos/${
    osSelection === "mac" ? "mac" : "windows"
  }-poster.webp`

  // Reload the video when the OS selection changes
  useEffect(() => {
    videoRef.current?.load()
  }, [videoSrc])

  return (
    <DialogContent size="4xl">
      <DialogHeader />

      <DialogBody className={styles.wrapper} data-unmask>
        <div className={styles.textContainer}>
          <DialogTitle variant="h2">
            {t("global.modals.microphoneNotShared.title")}
          </DialogTitle>
          <h3 className={styles.subtitle}>
            {t(`global.modals.microphoneNotShared.${osSelection}.subtitle`)}
          </h3>
          <ol className={styles.list}>
            {times(iterations).map((i) => (
              <li key={i} className={styles.listItem}>
                {t(
                  `global.modals.microphoneNotShared.${osSelection}.text.${i}`,
                )}
              </li>
            ))}
          </ol>
          <div className={styles.buttonsContainer}>
            <Button
              variant="outline"
              onClick={() => setOSSelection("win")}
              isDisabled={osSelection === "win"}
            >
              <div className={styles.buttonWrapper}>
                <Icon name="chevron" size="sm" className={styles.iconLeft} />
                <span>{t("global.modals.microphoneNotShared.win.button")}</span>
              </div>
            </Button>
            <Button
              variant="outline"
              onClick={() => setOSSelection("mac")}
              isDisabled={osSelection === "mac"}
            >
              <div className={styles.buttonWrapper}>
                <span>{t("global.modals.microphoneNotShared.mac.button")}</span>
                <Icon name="chevron" size="sm" />
              </div>
            </Button>
          </div>
        </div>

        <video
          autoPlay
          muted={true}
          loop={true}
          className={styles.videoContainer}
          ref={videoRef}
          poster={posterSrc}
          preload="auto"
        >
          <source src={videoSrc} type="video/mp4" />
          <a href={videoSrc}>{t("global.button.download")}</a>
        </video>
      </DialogBody>
    </DialogContent>
  )
}

const useMicrophoneErrorModal = () => {
  const { showModal } = useModal()

  const showMicrophoneErrorModal = async () => {
    await showModal(() => <MicrophoneErrorModal />)
  }

  return {
    showMicrophoneErrorModal,
  }
}

export { useMicrophoneErrorModal }
